/**
 * Warehouse Worker Redux Slice (card zkrTmTtm50PM — "W2·W4" warehouse floor app)
 *
 * The warehouse floor runs on a shared tablet signed into one office account.
 * Individual production workers PIN in to claim their assigned activities; the
 * PIN resolves to a warehouse worker's `user_profile_id` (see
 * server/routes/workflows/warehouse-workers-workflow.ts `POST /resolve-pin`).
 *
 * Session model = "stay until PIN-out / next PIN": once a worker PINs in they
 * remain the active worker (name shown on screen, completions credited to them)
 * until they explicitly PIN out OR another worker PINs in. That override is the
 * ONLY thing this slice holds — the underlying auth session is untouched.
 *
 * Persistence: mirrored to sessionStorage so the active worker survives a page
 * refresh on the same tablet. It's deliberately NOT localStorage (a tab close /
 * new day should not silently keep crediting the last worker) and it's cleared
 * on field logout (see SettingsPage) so a worker never persists past an office
 * sign-out.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../index.js';

export interface ActiveWarehouseWorker {
  userProfileId: string;
  fullName: string;
  productionDept: string[] | null;
}

interface WarehouseWorkerState {
  active: ActiveWarehouseWorker | null;
}

/** sessionStorage key for the persisted active-worker override. */
const STORAGE_KEY = 'lithos.warehouseWorker';

/** Read the persisted worker from sessionStorage (SSR/no-window safe). */
function readPersisted(): ActiveWarehouseWorker | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveWarehouseWorker;
    if (parsed && typeof parsed.userProfileId === 'string') return parsed;
    return null;
  } catch {
    return null;
  }
}

/** Write-through the active worker to sessionStorage (SSR/no-window safe). */
function writePersisted(worker: ActiveWarehouseWorker | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (worker) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(worker));
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // sessionStorage can throw in private-mode/quota edge cases — the in-memory
    // Redux state is still correct, so a persistence miss is non-fatal.
  }
}

const initialState: WarehouseWorkerState = {
  active: readPersisted(),
};

const warehouseWorkerSlice = createSlice({
  name: 'warehouseWorker',
  initialState,
  reducers: {
    setActiveWarehouseWorker: (state, action: PayloadAction<ActiveWarehouseWorker>) => {
      state.active = action.payload;
      writePersisted(action.payload);
    },
    clearActiveWarehouseWorker: (state) => {
      state.active = null;
      writePersisted(null);
    },
  },
});

export const { setActiveWarehouseWorker, clearActiveWarehouseWorker } =
  warehouseWorkerSlice.actions;

export const selectActiveWarehouseWorker = (state: RootState) =>
  state.warehouseWorker.active;

export default warehouseWorkerSlice.reducer;
